import { useState, useEffect } from 'react';

// This component shows real-time browser sessions in iframes
const BrowserSessions = ({ browserSessions = [], isInProgress = false }) => {
  const [expandedSessions, setExpandedSessions] = useState({});
  
  // Automatically expand all sessions if the job is in progress
  useEffect(() => {
    if (isInProgress && browserSessions.length > 0) {
      const initialExpanded = {};
      // Only auto-expand active sessions
      browserSessions
        .filter(session => session.status === 'active')
        .forEach(session => {
          initialExpanded[session.sessionId] = true;
        });
      setExpandedSessions(initialExpanded);
    }
  }, [browserSessions, isInProgress]);
  
  // Toggle session expansion
  const toggleSession = (sessionId) => {
    setExpandedSessions(prev => ({
      ...prev,
      [sessionId]: !prev[sessionId]
    }));
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (!browserSessions || browserSessions.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-4 mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Browser Sessions</h3>
        <div className="text-center py-8 text-gray-500">
          No browser sessions available for this job.
        </div>
      </div>
    );
  }

  // Group sessions by status
  const activeSessions = browserSessions.filter(session => session.status === 'active');
  const closedSessions = browserSessions.filter(session => session.status !== 'active');

  return (
    <div className="bg-white shadow rounded-lg p-4 mt-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Browser Sessions</h3>
      
      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <div className="mb-6">
          <h4 className="text-md font-medium text-gray-700 mb-3">Active Sessions</h4>
          
          <div className="space-y-4">
            {activeSessions.map(session => (
              <div key={session.sessionId} className="border border-gray-200 rounded-lg overflow-hidden">
                <div 
                  className="bg-gray-50 p-3 border-b flex justify-between items-center cursor-pointer"
                  onClick={() => toggleSession(session.sessionId)}
                >
                  <div>
                    <div className="flex items-center">
                      <div className="h-2 w-2 rounded-full bg-green-500 mr-2"></div>
                      <h4 className="font-medium text-gray-900">
                        Session ID: {session.sessionId.substring(0, 8)}...
                      </h4>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Started: {formatDate(session.startTime)}
                    </p>
                  </div>
                  <svg 
                    className={`w-5 h-5 transform transition-transform ${expandedSessions[session.sessionId] ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
                
                {expandedSessions[session.sessionId] && (
                  <>
                    {/* Live View Iframe */}
                    <div className="h-96 bg-gray-100">
                      {session.liveViewLink ? (
                        <iframe
                          src={session.liveViewLink}
                          title={`Browser Session ${session.sessionId}`}
                          className="w-full h-full border-0"
                          sandbox="allow-scripts allow-same-origin allow-forms"
                          allow="fullscreen"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                          <svg className="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"></path>
                          </svg>
                          <p>Live view not available</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Session Info */}
                    <div className="p-3 bg-gray-50 border-t flex justify-between items-center">
                      <div className="text-xs text-gray-600">
                        <p>Worker ID: {session.workerId || 'N/A'}</p>
                        <p>Browserbase ID: {session.browserBaseId || 'N/A'}</p>
                      </div>
                      {session.liveViewLink && (
                        <a
                          href={session.liveViewLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                          </svg>
                          Open in New Window
                        </a>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Closed Sessions */}
      {closedSessions.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-gray-700 mb-3">Closed Sessions</h4>
          
          <div className="space-y-4">
            {closedSessions.map(session => (
              <div key={session.sessionId} className="border border-gray-200 rounded-lg overflow-hidden">
                <div 
                  className="bg-gray-50 p-3 border-b flex justify-between items-center cursor-pointer"
                  onClick={() => toggleSession(session.sessionId)}
                >
                  <div>
                    <div className="flex items-center">
                      <div className="h-2 w-2 rounded-full bg-gray-400 mr-2"></div>
                      <h4 className="font-medium text-gray-900">
                        Session ID: {session.sessionId.substring(0, 8)}...
                      </h4>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {session.startTime && `Started: ${formatDate(session.startTime)}`}
                      {session.endTime && ` • Ended: ${formatDate(session.endTime)}`}
                    </p>
                  </div>
                  <svg 
                    className={`w-5 h-5 transform transition-transform ${expandedSessions[session.sessionId] ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
                
                {expandedSessions[session.sessionId] && (
                  <>
                    {/* Session Info */}
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Session ID</p>
                          <p className="font-medium text-gray-900">{session.sessionId}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Status</p>
                          <p className="font-medium text-gray-900">{session.status}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Worker ID</p>
                          <p className="font-medium text-gray-900">{session.workerId || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Browserbase ID</p>
                          <p className="font-medium text-gray-900">{session.browserBaseId || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Start Time</p>
                          <p className="font-medium text-gray-900">{formatDate(session.startTime)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">End Time</p>
                          <p className="font-medium text-gray-900">{formatDate(session.endTime)}</p>
                        </div>
                      </div>
                      
                      {/* No live view available for closed sessions */}
                      <div className="mt-4 bg-gray-100 rounded-md p-4 text-center text-gray-500">
                        <p>Live view is no longer available for closed sessions</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BrowserSessions;